import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';
import {
	OrderStatesEnum,
	OrderStatusesEnum,
} from '../../../services/orders.services';
import {
	progressRank,
	resolveExecutionStatus,
} from '../../../utils/order-execution.util';
import { OrderTarget } from '../../../components/OrdersTable/interfaces/OrderStatus.interface';
import { NotificationsService } from '../../../services/notifications.services';
import { BillingService } from '../../../services/billing.service';
import { OrderDetailsContext } from '../../../context/OrderDetailsContext/order-details.context';
import Emitter, { EventType } from '../../../services/events';
import Paid from './components/Steps/Paid';
import Notifications from './components/Notifications';
import DocumentsTab from '../../../components/DocumentsTab/DocumentsTab';
import AssignOrder from './components/Steps/AssignOrder';
import InProgress from './components/Steps/InProgress';
import ConfirmPickUp from './components/Steps/ConfirmPickUp';
import PendingPayment from './components/Steps/PendingPayment';
import { OrderDetailsProps } from './producerOrder.interface';
import OpenOrderPageButton from '../../../components/OpenOrderPageButton/OpenOrderPageButton';
import OrderDetailsNav from '../../../components/OrderDetailsNav/OrderDetailsNav';
import { TabConfig } from '../../../components/OrderDetailsTabButton/OrderDetailsTabButton.interface';
import TabButton from '../../../components/OrderDetailsTabButton/OrderDetailsTabButton';
import {
	Contract,
	ContractsService,
} from '../../../services/contracts.service';
import ArrowToLeft from '../../../assets/ArrowToLeft';

const TAB_CONFIG: Record<string, TabConfig> = {
	overview: {
		icon: 'Document Text',
		label: 'tabButton.general',
		translatedLabel: 'General',
	},
	documents: {
		icon: 'Folder',
		label: 'tabButton.documents',
		translatedLabel: 'Documents',
	},
	events: {
		icon: 'Bell',
		label: 'tabButton.events',
		translatedLabel: 'Events',
	},
} as const;

const OrderDetails: React.FC<OrderDetailsProps> = ({
	isOpen = false,
	selectedOrder,
	onBack,
	hideViewContractButton = false,
	position = 'absolute',
}) => {
	const { t } = useTranslate();
	const { setProducerInvoice, setHideViewContractButton } =
		useContext(OrderDetailsContext);
	const [order, setOrder] = useState<OrderInterface | undefined>(selectedOrder);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);
	const [currentTab, setCurrentTab] =
		useState<keyof typeof TAB_CONFIG>('overview');
	const [contract, setContract] = useState<Contract>();

	const notifications = useMemo(
		() => selectedOrder?.notifications?.items ?? [],
		[selectedOrder?.notifications?.items],
	);

	const getInvoice = useCallback(async () => {
		if (
			!selectedOrder?.id ||
			!selectedOrder?.target ||
			(selectedOrder.deliveryIncluded
				? ![
						OrderStatesEnum.LOADED,
						OrderStatesEnum.TO_UNLOADING,
						OrderStatesEnum.ARRIVED_TO_UNLOADING,
						OrderStatesEnum.UNLOADING,
						OrderStatesEnum.UNLOADED,
						OrderStatesEnum.COMPLETED,
				  ].includes(selectedOrder.state)
				: ![OrderStatusesEnum.PENDING_PAYMENT, OrderStatusesEnum.PAID].includes(
						selectedOrder.status,
				  ))
		) {
			setIsLoading(false);
			setIsInitialized(true);
			return;
		}

		try {
			const { data } = await BillingService.getInvoiceById(
				selectedOrder.target,
				selectedOrder.id,
			);
			setProducerInvoice(data);
		} catch (e) {
		} finally {
			setIsLoading(false);
			setIsInitialized(true);
		}
	}, [selectedOrder, setProducerInvoice]);

	const markReadNotifications = useCallback(async () => {
		const notificationIds = order?.notifications?.items
			?.filter((r) => !r.readAt)
			?.map((r) => r.id);
		try {
			if (notificationIds?.length) {
				await NotificationsService.readNotify(notificationIds);
				Emitter.emit(EventType.NOTIFICATION_COUNT_DECREASE, 1);
			}
			Emitter.emit(EventType.ORDER_NOTIFICATION_COUNT_DECREASE, order?.id!);
		} catch (e) {}
	}, [order]);

	const selectOrder = (order: OrderInterface) => {
		setOrder(order);
	};

	useEffect(() => {
		if (isOpen) {
			getInvoice().then(() => {});
		}
	}, [getInvoice, isOpen]);

	useEffect(() => {
		const refreshHandler = () => getInvoice();
		Emitter.on(EventType.PAYMENT_NOTE_REFRESH, refreshHandler);
		return () => {
			Emitter.off(EventType.PAYMENT_NOTE_REFRESH, refreshHandler);
		};
	}, [getInvoice]);

	useEffect(() => {
		setOrder(selectedOrder);
		setCurrentTab('overview');
	}, [selectedOrder]);

	useEffect(() => {
		if (order?.id) {
			markReadNotifications().then(() => {});
		}
	}, [markReadNotifications, order?.id]);

	useEffect(() => {
		let isActive = true;

		const fetchContract = async () => {
			try {
				if (!selectedOrder?.contractId) {
					setContract(undefined);
					return;
				}

				const { data } = await ContractsService.getContractById(
					selectedOrder.contractId,
					'producer',
				);
				if (isActive) setContract(data);
			} catch (e) {}
		};

		fetchContract().then(() => {});
		return () => {
			isActive = false;
		};
	}, [selectedOrder?.contractId]);

	useEffect(() => {
		setHideViewContractButton(!!hideViewContractButton);
	}, [hideViewContractButton, setHideViewContractButton]);

	const renderSimple = (order: OrderInterface) => {
		if (order.status === OrderStatusesEnum.PAID) {
			return <Paid selectedOrder={order} contract={contract} />;
		}

		const execution = resolveExecutionStatus(order);

		// The seller still has to accept the order even if the carrier has
		// already sent a truck to a driver.
		const executionRank = progressRank(execution);
		if (
			order.status !== OrderStatusesEnum.IN_PROGRESS &&
			executionRank >= progressRank(OrderStatesEnum.TRANSPORTER_CONFIRMED) &&
			executionRank <= progressRank(OrderStatusesEnum.ACCEPTED)
		) {
			return (
				<AssignOrder
					selectedOrder={order}
					contract={contract}
					setOrder={selectOrder}
				/>
			);
		}

		switch (execution) {
			case OrderStatesEnum.TRANSPORTER_CONFIRMED:
			case OrderStatesEnum.TRUCK_CONFIRMED:
			case OrderStatusesEnum.REQUESTED:
			case OrderStatusesEnum.ACCEPTED:
			case OrderStatesEnum.TO_LOADING:
				return (
					<InProgress
						selectedOrder={order}
						contract={contract}
						setOrder={selectOrder}
					/>
				);
			case OrderStatesEnum.ARRIVED_TO_LOADING:
			case OrderStatesEnum.LOADING:
				return (
					<ConfirmPickUp
						selectedOrder={order}
						contract={contract}
						setOrder={selectOrder}
					/>
				);
			case OrderStatesEnum.LOADED:
			case OrderStatesEnum.TO_UNLOADING:
			case OrderStatesEnum.ARRIVED_TO_UNLOADING:
			case OrderStatesEnum.UNLOADING:
			case OrderStatesEnum.UNLOADED:
			case OrderStatesEnum.COMPLETED:
				return <PendingPayment selectedOrder={order} contract={contract} />;
			default:
				return null;
		}
	};

	const renderExcluded = (order: OrderInterface) => {
		const statusComponentMap: { [key: string]: JSX.Element } = {
			[OrderStatesEnum.TO_DO]: (
				<AssignOrder
					selectedOrder={order}
					contract={contract}
					setOrder={selectOrder}
				/>
			),
			[OrderStatesEnum.IN_PROGRESS]: (
				<InProgress
					selectedOrder={order}
					contract={contract}
					setOrder={selectOrder}
				/>
			),
			[OrderStatusesEnum.CONFIRM_PICKUP]: (
				<ConfirmPickUp
					selectedOrder={order}
					contract={contract}
					setOrder={selectOrder}
				/>
			),
			[OrderStatusesEnum.PENDING_PAYMENT]: (
				<PendingPayment selectedOrder={order} contract={contract} />
			),
			[OrderStatusesEnum.PAID]: (
				<Paid selectedOrder={order} contract={contract} />
			),
		};

		return statusComponentMap[order.status] || null;
	};

	const renderOverviewTab = (order: OrderInterface | undefined) => {
		if (!order) return null;
		const { deliveryIncluded } = order;

		return deliveryIncluded ? renderSimple(order) : renderExcluded(order);
	};

	return isOpen && isInitialized && !isLoading ? (
		<aside
			className={`w-[716px] h-full flex flex-col ${position} top-0 right-0 bg-alabaster shadow-order-details z-[100] overflow-y-auto scroll-smooth rounded-2xl`}
		>
			<OrderDetailsNav
				tabs={
					<>
						{onBack && (
							<SypacButton variant="secondary" className="ml-[-24px] mr-3">
								<button
									type="button"
									className="flex gap-2 border-none h-[24px] bg-transparent group"
									onClick={onBack}
								>
									<span className="mt-[4px] transition group-hover:text-gray-80 group-hover:-translate-x-[6px]">
										<ArrowToLeft width={38.5} height={16.5} />
									</span>
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 mt-[2px]">
											<T keyName="clientSelectCategory.backButton">Back</T>
										</p>
									</SypacText>
								</button>
							</SypacButton>
						)}

						{Object.keys(TAB_CONFIG).map((tab) => (
							<TabButton
								key={tab}
								tab={tab}
								isActive={currentTab === tab}
								config={TAB_CONFIG[tab]}
								notificationCount={
									tab === 'events' ? notifications.length : undefined
								}
								onClick={setCurrentTab}
								target={OrderTarget.producer}
							/>
						))}
					</>
				}
				maxCompactLevel={1}
				renderActions={(level) =>
					selectedOrder?.id && order?.trucks?.length ? (
						<OpenOrderPageButton
							orderId={selectedOrder.id}
							iconOnly={level >= 1}
						/>
					) : null
				}
			/>

			<main className="flex flex-col h-full overflow-x-hidden overflow-y-scroll sypac-scrollbar">
				{currentTab === 'overview' && order && renderOverviewTab(order)}

				{currentTab === 'events' && (
					<section className="flex flex-col gap-8 px-10 py-7 border-0 border-solid border-t-[1px] border-gray-10">
						<SypacText variant="body-regular-medium">
							<p className="text-base leading-6 tracking-tighter text-gray-40">
								{t('awaitingTransport.timeline', 'Timeline')}
							</p>
						</SypacText>
						<Notifications selectedOrder={order} />
					</section>
				)}

				{currentTab === 'documents' && (
					<section className="flex flex-col gap-8 pt-8 border-0 border-solid border-t-[1px] border-gray-10">
						<DocumentsTab selectedOrder={order} target={OrderTarget.producer} />
					</section>
				)}
			</main>
		</aside>
	) : null;
};

export default OrderDetails;
